import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useArtistAuth } from '../../../contexts/ArtistAuthContext';
import { useToast } from '../../common/ToastSystem';
import useChatProfileModalState from '../../common/chat/members/useChatProfileModalState';
import useChatNotesBadgeCount from '../../common/chat/notes/useChatNotesBadgeCount';
import useChatAnnouncements from '../../common/chat/announcements/useChatAnnouncements';
import useDelayedVisibility from '../../common/chat/shared/useDelayedVisibility';
import useChatMessageReactions from '../../common/chat/reactions/useChatMessageReactions';
import { focusChatAnchorMessage } from '../../common/chat/timeline/chatMessageAnchorUtils';
import {
  mergeMessages,
} from '../../common/chat/timeline/timelineMerge';
import { listParts } from '../../../api/artists';
import {
  getArtistGroupChatThread,
  listArtistGroupChatMessages,
  listArtistGroupChatMessagesAround,
  markArtistGroupChatMessagesRead,
} from '../../../api/artistChat';
import {
  GROUP_CHAT_PAGE_SIZE,
  buildArtistGroupNotesListUrl,
  groupPaneDbg,
  mergeThreadSummaryIntoThread,
} from './artistGroupChatPaneUtils';
import ArtistGroupChatPaneView from './ArtistGroupChatPaneView';
import useArtistGroupChatSocket from './useArtistGroupChatSocket';
import '../../../css/artist/chats/ArtistGroupChatPane.css';
import '../../../css/common/chat/ChatDragDrop.css';
import useArtistGroupChatImages from './useArtistGroupChatImages';
import useArtistGroupChatMembers from './useArtistGroupChatMembers';
import useArtistGroupChatMessageActions from './useArtistGroupChatMessageActions';
import useArtistGroupChatFileDrop from './useArtistGroupChatFileDrop';

const ArtistGroupChatPane = ({
  threadId,
  anchorMessageId = '',
  threadSummary,
  readOnly = false,
  onThreadMeta,
  onThreadsRefresh,
  onAccessLost,
  showMobileBackButton = false,
  onMobileBack = null,
}) => {
  groupPaneDbg('render', {
    threadId: threadId || null,
    anchorMessageId: String(anchorMessageId || '').trim() || null,
    threadSummaryId: String(threadSummary?.thread_id || '') || null,
    showMobileBackButton,
  });
  const { artistUser } = useArtistAuth();
  const { error: toastError, success: toastSuccess } = useToast();
  const [thread, setThread] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const { noteCount, refreshNoteCount } = useChatNotesBadgeCount({
    ownerId: threadId,
    buildListUrl: buildArtistGroupNotesListUrl,
    enabled: Boolean(threadId),
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadedMessagesThreadId, setLoadedMessagesThreadId] = useState('');
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [loadingNewerMessages, setLoadingNewerMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [hasNewerMessages, setHasNewerMessages] = useState(false);
  const [messageWindowOffset, setMessageWindowOffset] = useState(0);
  const [activeAnchorMessageId, setActiveAnchorMessageId] = useState('');
  const [replyDraft, setReplyDraft] = useState(null);
  const {
    closeImageModal,
    displayMessages,
    imageItems,
    imgModalIndex,
    imgModalOpen,
    openImageModal,
    resetImageModal,
    showNextImage,
    showPrevImage,
  } = useArtistGroupChatImages(messages);
  const {
    announcements,
    latestAnnouncement,
    announcementCount,
    listOpen: isAnnouncementListOpen,
    setListOpen: setIsAnnouncementListOpen,
    refreshAnnouncements,
    pinAnnouncement,
    unpinAnnouncement,
    isAnnouncedMessage,
    actionMessageId: announcementActionMessageId,
  } = useChatAnnouncements({
    mode: 'artist',
    scope: 'group',
    threadId: threadId || '',
    enabled: Boolean(threadId),
    onError: toastError,
  });
  const {
    detailOpen: isReactionDetailOpen,
    detailGroups: reactionDetailGroups,
    detailLoading: reactionDetailLoading,
    openReactionDetail,
    closeReactionDetail,
    toggleReaction,
    refreshMessageReactionDetails,
    isReactionPending,
  } = useChatMessageReactions({
    mode: 'artist',
    scope: 'group',
    threadId: threadId || '',
    enabled: Boolean(threadId),
    setMessages,
    onError: toastError,
  });
  const {
    profileTarget,
    closeProfileModal,
    openAdminProfile,
    openArtistProfile,
    openProfileTarget,
  } = useChatProfileModalState();
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [parts, setParts] = useState([]);
  const messagesContainerRef = useRef(null);
  const chatDropScopeRef = useRef(null);
  const anchorHighlightTimerRef = useRef(null);
  const shouldStickLatestRef = useRef(true);
  const previousThreadIdRef = useRef('');


  useEffect(() => {
    setReplyDraft(null);
  }, [threadId]);

  const isViewingLatest = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return Math.abs(container.scrollTop) <= 80;
  }, []);

  const scrollToLatest = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = 0;
  }, []);

  const loadThread = useCallback(async () => {
    if (!threadId) {
      groupPaneDbg('loadThread:skip-no-thread');
      setThread(null);
      return null;
    }
    groupPaneDbg('loadThread:start', {
      threadId,
      threadSummaryId: String(threadSummary?.thread_id || '') || null,
    });
    setLoading(true);
    try {
      const data = await getArtistGroupChatThread(threadId);
      const next = data?.thread || null;
      groupPaneDbg('loadThread:success', {
        threadId,
        fetchedThreadId: String(next?.thread_id || '') || null,
        assigneeCount: Array.isArray(next?.assignees) ? next.assignees.length : 0,
        artistCount: Array.isArray(next?.artists) ? next.artists.length : 0,
        status: next?.status || null,
      });
      setThread(next);
      if (next && typeof onThreadMeta === 'function') {
        onThreadMeta(next.thread_id, next);
      }
      return next;
    } catch (error) {
      groupPaneDbg('loadThread:error', {
        threadId,
        status: Number(error?.status) || null,
        message: error?.message || String(error),
      });
      if (typeof onAccessLost === 'function' && (error?.status === 403 || error?.status === 404)) {
        onAccessLost(threadId, error?.message || 'グループチャットを閲覧できません');
      } else {
        toastError(error?.message || 'グループチャット詳細の取得に失敗しました');
      }
      setThread(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [onAccessLost, onThreadMeta, threadId, threadSummary?.thread_id, toastError]);

  const loadMessages = useCallback(async ({ anchorMessageId: anchorId = '', silent = false } = {}) => {
    if (!threadId) {
      groupPaneDbg('loadMessages:skip-no-thread');
      setMessages([]);
      setLoadedMessagesThreadId('');
      setHasMoreMessages(false);
      setHasNewerMessages(false);
      setMessageWindowOffset(0);
      setActiveAnchorMessageId('');
      return [];
    }
    const normalizedAnchorId = String(anchorId || '').trim();
    setLoadedMessagesThreadId('');
    groupPaneDbg('loadMessages:start', {
      threadId,
      anchorMessageId: normalizedAnchorId || null,
    });
    if (!silent) {
      setLoadingMessages(true);
    }
    try {
      const data = normalizedAnchorId
        ? await listArtistGroupChatMessagesAround(threadId, normalizedAnchorId, 25, 25)
        : await listArtistGroupChatMessages(threadId, 0, GROUP_CHAT_PAGE_SIZE);
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      groupPaneDbg('loadMessages:success', {
        threadId,
        anchorMessageId: normalizedAnchorId || null,
        count: rows.length,
        firstMessageId: rows[0]?.id || null,
        hasMore: normalizedAnchorId ? Boolean(data?.hasOlder) : Boolean(data?.hasMore),
        hasNewer: normalizedAnchorId ? Boolean(data?.hasNewer) : false,
      });
      // wire は DESC(§1)。全流入を merge に通し DESC+一意をコードで保証する(§3.2)。
      setMessages(mergeMessages([], rows));
      if (normalizedAnchorId) {
        setHasMoreMessages(Boolean(data?.hasOlder));
        setHasNewerMessages(Boolean(data?.hasNewer));
        setMessageWindowOffset(Number.isFinite(Number(data?.offset)) ? Number(data.offset) : 0);
        focusChatAnchorMessage({
          messageId: normalizedAnchorId,
          containerRef: messagesContainerRef,
          setActiveAnchorMessageId,
          timerRef: anchorHighlightTimerRef,
          durationMs: 3500,
        });
      } else {
        setHasMoreMessages(Boolean(data?.hasMore) || rows.length >= GROUP_CHAT_PAGE_SIZE);
        setHasNewerMessages(false);
        setMessageWindowOffset(0);
        setActiveAnchorMessageId('');
      }
      // §3.6: ANCHOR ジャンプで開いた時は最新未到達のため既読化を保留。
      const newest = rows[0]?.id;
      if (newest && !normalizedAnchorId) {
        await markArtistGroupChatMessagesRead({ threadId, lastReadMessageId: newest }).catch(() => {});
      }
      if (typeof onThreadMeta === 'function') {
        if (normalizedAnchorId) {
          onThreadMeta(threadId, { latest_message: rows[0] || null });
        } else {
          onThreadMeta(threadId, { unread_count: 0, latest_message: rows[0] || null });
        }
      }
      return rows;
    } catch (error) {
      groupPaneDbg('loadMessages:error', {
        threadId,
        anchorMessageId: normalizedAnchorId || null,
        status: Number(error?.status) || null,
        message: error?.message || String(error),
      });
      if (normalizedAnchorId && Number(error?.status) === 404) {
        try {
          const fallbackData = await listArtistGroupChatMessages(threadId, 0, GROUP_CHAT_PAGE_SIZE);
          const fallbackRows = Array.isArray(fallbackData?.messages) ? fallbackData.messages : [];
          groupPaneDbg('loadMessages:fallback-latest-success', {
            threadId,
            anchorMessageId: normalizedAnchorId || null,
            count: fallbackRows.length,
            firstMessageId: fallbackRows[0]?.id || null,
          });
          setMessages(mergeMessages([], fallbackRows));
          setHasMoreMessages(
            Boolean(fallbackData?.hasMore) || fallbackRows.length >= GROUP_CHAT_PAGE_SIZE
          );
          setHasNewerMessages(false);
          setMessageWindowOffset(0);
          setActiveAnchorMessageId('');
          const newest = fallbackRows[0]?.id;
          if (newest) {
            await markArtistGroupChatMessagesRead({ threadId, lastReadMessageId: newest }).catch(() => {});
          }
          if (typeof onThreadMeta === 'function') {
            onThreadMeta(threadId, { unread_count: 0, latest_message: fallbackRows[0] || null });
          }
          toastError('対象メッセージが見つからないため、最新メッセージを表示しました');
          return fallbackRows;
        } catch (_) {}
      }
      if (silent) {
        return [];
      }
      if (typeof onAccessLost === 'function' && (error?.status === 403 || error?.status === 404)) {
        onAccessLost(threadId, error?.message || 'グループチャットを閲覧できません');
      } else {
        toastError(error?.message || 'グループチャットのメッセージ取得に失敗しました');
      }
      setMessages([]);
      setHasMoreMessages(false);
      setHasNewerMessages(false);
      setMessageWindowOffset(0);
      setActiveAnchorMessageId('');
      return [];
    } finally {
      setLoadedMessagesThreadId(String(threadId || '').trim());
      if (!silent) {
        setLoadingMessages(false);
      }
    }
  }, [onAccessLost, onThreadMeta, threadId, toastError]);

  // NOTE(§4.1.2): 旧 scheduleRealtimeReconcile(700ms 後に最新ページ+スレッド詳細+一覧を
  // 再取得)は廃止。socket 受信は enriched=直接 merge / skeleton=最新ページ silent merge の
  // 2 経路に集約。スレッド一覧の鮮度は onThreadsRefresh/onThreadMeta が担う(§3.7)。

  const loadOlderMessages = useCallback(async () => {
    if (!threadId || loadingMessages || loadingOlderMessages || !hasMoreMessages) return;
    setLoadingOlderMessages(true);
    try {
      const container = messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;
      const previousScrollTop = container?.scrollTop || 0;
      const isReverseColumn = container
        ? (getComputedStyle(container).flexDirection || '').toLowerCase().includes('column-reverse')
        : true;
      const currentCount = Array.isArray(messages) ? messages.length : 0;
      const nextOffset = messageWindowOffset + currentCount;
      const data = await listArtistGroupChatMessages(threadId, nextOffset, GROUP_CHAT_PAGE_SIZE);
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      setMessages((prev) => mergeMessages(prev, rows));
      setHasMoreMessages(Boolean(data?.hasMore) || rows.length >= GROUP_CHAT_PAGE_SIZE);
      requestAnimationFrame(() => {
        if (!messagesContainerRef.current) return;
        const nextScrollHeight = messagesContainerRef.current.scrollHeight;
        const heightDelta = nextScrollHeight - previousScrollHeight;
        messagesContainerRef.current.scrollTop = isReverseColumn
          ? previousScrollTop - heightDelta
          : previousScrollTop + heightDelta;
      });
    } catch (error) {
      toastError(error?.message || '過去メッセージの取得に失敗しました');
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [hasMoreMessages, loadingMessages, loadingOlderMessages, messageWindowOffset, messages, threadId, toastError]);

  const loadNewerMessages = useCallback(async () => {
    if (!threadId || loadingMessages || loadingNewerMessages || !hasNewerMessages) return;
    setLoadingNewerMessages(true);
    try {
      const previousScrollHeight = messagesContainerRef.current?.scrollHeight || 0;
      const nextOffset = Math.max(0, messageWindowOffset - GROUP_CHAT_PAGE_SIZE);
      const fetchLimit = Math.max(1, messageWindowOffset - nextOffset);
      const data = await listArtistGroupChatMessages(threadId, nextOffset, fetchLimit);
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      setMessages((prev) => mergeMessages(prev, rows));
      setMessageWindowOffset(nextOffset);
      setHasNewerMessages(nextOffset > 0);
      // §3.6: offset 0 に達したら LIVE 復帰 → 最新で既読化(ANCHOR からの収束)。
      if (nextOffset === 0 && rows[0]?.id) {
        markArtistGroupChatMessagesRead({ threadId, lastReadMessageId: rows[0].id })
          .then(() => {
            if (typeof onThreadMeta === 'function') onThreadMeta(threadId, { unread_count: 0 });
          })
          .catch(() => {});
      }
      requestAnimationFrame(() => {
        if (!messagesContainerRef.current) return;
        const nextScrollHeight = messagesContainerRef.current.scrollHeight;
        messagesContainerRef.current.scrollTop = Math.max(
          0,
          nextScrollHeight - previousScrollHeight + messagesContainerRef.current.scrollTop
        );
      });
    } catch (error) {
      toastError(error?.message || '新しいメッセージの取得に失敗しました');
    } finally {
      setLoadingNewerMessages(false);
    }
  }, [
    hasNewerMessages,
    loadingMessages,
    loadingNewerMessages,
    messageWindowOffset,
    onThreadMeta,
    threadId,
    toastError,
  ]);

  const isViewingLatestMessages = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const direction = (getComputedStyle(container).flexDirection || '').toLowerCase();
    const nearBottomNormal =
      container.scrollHeight - container.clientHeight - container.scrollTop <= 80;
    const nearBottomReverse = Math.abs(container.scrollTop) <= 80;
    return direction.includes('column-reverse') ? nearBottomReverse : nearBottomNormal;
  }, []);

  const isViewingOldestMessages = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return false;
    const direction = (getComputedStyle(container).flexDirection || '').toLowerCase();
    if (direction.includes('column-reverse')) {
      return container.scrollHeight - container.clientHeight + container.scrollTop <= 80;
    }
    return container.scrollTop <= 80;
  }, []);

  const handleMessagesScroll = useCallback(() => {
    if (
      hasMoreMessages &&
      !loadingMessages &&
      !loadingOlderMessages &&
      isViewingOldestMessages()
    ) {
      loadOlderMessages();
    }
    if (
      hasNewerMessages &&
      !loadingMessages &&
      !loadingNewerMessages &&
      isViewingLatestMessages()
    ) {
      loadNewerMessages();
    }
  }, [
    hasMoreMessages,
    hasNewerMessages,
    isViewingOldestMessages,
    isViewingLatestMessages,
    loadOlderMessages,
    loadNewerMessages,
    loadingMessages,
    loadingOlderMessages,
    loadingNewerMessages,
  ]);

  useEffect(() => {
    if (
      hasMoreMessages &&
      !loadingMessages &&
      !loadingOlderMessages &&
      isViewingOldestMessages()
    ) {
      loadOlderMessages();
    }
  }, [
    hasMoreMessages,
    isViewingOldestMessages,
    loadOlderMessages,
    loadingMessages,
    loadingOlderMessages,
    messages.length,
  ]);

  useEffect(() => {
    const target = messagesContainerRef.current;
    if (!target) return undefined;
    target.addEventListener('scroll', handleMessagesScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleMessagesScroll);
  }, [handleMessagesScroll]);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const payload = await listParts();
        if (!aborted) {
          setParts(Array.isArray(payload) ? payload : []);
        }
      } catch (_) {
        if (!aborted) setParts([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  useEffect(() => {
    if (!threadId) return undefined;
    let cancelled = false;
    groupPaneDbg('thread-bootstrap:start', {
      threadId,
      anchorMessageId: String(anchorMessageId || '').trim() || null,
    });
    (async () => {
      await Promise.all([loadThread(), loadMessages({ anchorMessageId })]);
      groupPaneDbg('thread-bootstrap:resolved', {
        threadId,
        cancelled,
      });
      if (!cancelled && typeof onThreadsRefresh === 'function') {
        onThreadsRefresh(true);
      }
    })();
    return () => {
      groupPaneDbg('thread-bootstrap:cleanup', { threadId });
      cancelled = true;
    };
  }, [anchorMessageId, loadMessages, loadThread, onThreadsRefresh, threadId]);

  useArtistGroupChatSocket({
    loadMessages,
    loadThread,
    onThreadMeta,
    onThreadsRefresh,
    refreshAnnouncements,
    refreshMessageReactionDetails,
    setMessages,
    threadId,
  });

  useEffect(() => {
    setIsMemberModalOpen(false);
    resetImageModal();
  }, [resetImageModal, threadId]);

  useEffect(
    () => () => {
      if (anchorHighlightTimerRef.current) {
        clearTimeout(anchorHighlightTimerRef.current);
        anchorHighlightTimerRef.current = null;
      }
    },
    []
  );

  const handleComposerSent = useCallback(async () => {
    shouldStickLatestRef.current = isViewingLatest();
    await Promise.all([loadMessages(), loadThread()]);
    if (typeof onThreadsRefresh === 'function') onThreadsRefresh(true);
    setReplyDraft(null);
    if (shouldStickLatestRef.current) {
      requestAnimationFrame(() => {
        scrollToLatest();
      });
    }
  }, [isViewingLatest, loadMessages, loadThread, onThreadsRefresh, scrollToLatest]);

  const currentThreadKey = String(threadId || '').trim();
  const shouldShowEmptyMessages = Boolean(
    currentThreadKey &&
      loadedMessagesThreadId === currentThreadKey &&
      !loading &&
      !loadingMessages &&
      !loadingOlderMessages &&
      !loadingNewerMessages &&
      displayMessages.length === 0
  );

  const {
    canAnnouncementMessage,
    canCopyMessage,
    canReactMessage,
    canReplyMessage,
    canUnsendMessage,
    clearMessageLongPress,
    closeMessageMenu,
    handleCopyMessage,
    handleMessageContextMenu,
    handleMessageMenuReactionSelect,
    handleMessageTouchStart,
    handleOpenInlinePartialCopy,
    handleSelectReplyMessage,
    handleToggleAnnouncementMessage,
    handleUnsendMessage,
    inlineCopyMessageId,
    messageMenu,
    messageMenuRef,
    renderPartialCopyContent,
    unsendingMessageId,
  } = useArtistGroupChatMessageActions({
    announcementActionMessageId,
    artistUser,
    isAnnouncedMessage,
    loadThread,
    messagesContainerRef,
    onThreadsRefresh,
    pinAnnouncement,
    setMessages,
    setReplyDraft,
    threadId,
    toastError,
    toastSuccess,
    toggleReaction,
    unpinAnnouncement,
  });

  const handleSelectAnnouncementMessage = useCallback(
    async (messageId) => {
      const normalizedMessageId = String(messageId || '').trim();
      if (!normalizedMessageId) return;
      setIsAnnouncementListOpen(false);
      await loadMessages({ anchorMessageId: normalizedMessageId });
    },
    [loadMessages, setIsAnnouncementListOpen],
  );

  const handleJumpToReplyMessage = useCallback(async (reply) => {
    const normalizedMessageId = String(reply?.messageId || '').trim();
    if (!normalizedMessageId) return;
    if (reply?.isUnavailable) {
      toastError('返信元メッセージが見つかりません。');
      return;
    }
    await loadMessages({ anchorMessageId: normalizedMessageId });
  }, [loadMessages, toastError]);

  const { isDragActive, overlayRect } = useArtistGroupChatFileDrop({
    artistUserId: artistUser?.userId,
    chatDropScopeRef,
    handleComposerSent,
    messagesContainerRef,
    readOnly,
    replyDraft,
    thread,
    threadId,
    toastError,
  });

  const {
    buildAdminProfileData,
    buildArtistProfileData,
    groupedSidePanelArtists,
    handleAvatarClick,
    handleMentionClick,
    members,
    mentionCandidates,
    participantCount,
  } = useArtistGroupChatMembers({
    artistUserId: artistUser?.userId,
    openAdminProfile,
    openArtistProfile,
    openProfileTarget,
    parts,
    setIsMemberModalOpen,
    thread,
  });
  const showInitialLoadingOverlay = (loading || loadingMessages) && messages.length === 0;
  const showInlineLoadingSpinner = loadingOlderMessages && messages.length > 0;
  const showDelayedInitialLoading = useDelayedVisibility(showInitialLoadingOverlay, 1000);

  useEffect(() => {
    groupPaneDbg('thread-state', {
      threadId: threadId || null,
      threadExists: Boolean(thread),
      fetchedThreadId: String(thread?.thread_id || '') || null,
      loading,
      loadingMessages,
      messagesCount: messages.length,
      assigneeCount: members.assignees.length,
      artistCount: members.artists.length,
      participantCount,
      threadSummaryId: String(threadSummary?.thread_id || '') || null,
    });
  }, [
    loading,
    loadingMessages,
    members.artists.length,
    members.assignees.length,
    messages.length,
    participantCount,
    thread,
    threadId,
    threadSummary?.thread_id,
  ]);

  useEffect(() => {
    const normalizedThreadId = String(threadId || '').trim();
    const previousThreadId = previousThreadIdRef.current;

    if (normalizedThreadId === previousThreadId) {
      return;
    }

    previousThreadIdRef.current = normalizedThreadId;

    if (!normalizedThreadId) {
      groupPaneDbg('thread-switch:clear-no-thread');
      setThread(null);
      setMessages([]);
      setLoadedMessagesThreadId('');
      setHasMoreMessages(false);
      setHasNewerMessages(false);
      setMessageWindowOffset(0);
      setActiveAnchorMessageId('');
      return;
    }
    // スレッド切替直後に直前のチャット内容が一瞬見えないよう、表示を即時切り替える。
    groupPaneDbg('thread-switch:prime-from-summary', {
      previousThreadId: previousThreadId || null,
      threadId: normalizedThreadId,
      threadSummaryId: String(threadSummary?.thread_id || '') || null,
      threadSummaryAssigneeCount: Array.isArray(threadSummary?.assignees)
        ? threadSummary.assignees.length
        : 0,
      threadSummaryArtistCount: Array.isArray(threadSummary?.artists)
        ? threadSummary.artists.length
        : 0,
    });
    setThread(threadSummary || null);
    setMessages([]);
    setLoadedMessagesThreadId('');
    setHasMoreMessages(false);
    setHasNewerMessages(false);
    setMessageWindowOffset(0);
    setActiveAnchorMessageId('');
  }, [threadId, threadSummary]);

  useEffect(() => {
    setShowNotes(false);
  }, [threadId]);

  useEffect(() => {
    if (!threadId) return;
    setThread((prev) => {
      const next = mergeThreadSummaryIntoThread(prev, threadSummary, threadId);
      groupPaneDbg('thread-summary:merge', {
        threadId,
        summaryThreadId: String(threadSummary?.thread_id || '') || null,
        previousAssigneeCount: Array.isArray(prev?.assignees) ? prev.assignees.length : 0,
        previousArtistCount: Array.isArray(prev?.artists) ? prev.artists.length : 0,
        summaryAssigneeCount: Array.isArray(threadSummary?.assignees)
          ? threadSummary.assignees.length
          : 0,
        summaryArtistCount: Array.isArray(threadSummary?.artists)
          ? threadSummary.artists.length
          : 0,
        nextAssigneeCount: Array.isArray(next?.assignees) ? next.assignees.length : 0,
        nextArtistCount: Array.isArray(next?.artists) ? next.artists.length : 0,
      });
      return next;
    });
  }, [threadId, threadSummary]);

  return (
    <ArtistGroupChatPaneView
      {...{
        activeAnchorMessageId, announcementActionMessageId, announcementCount, announcements, artistUser,
        buildAdminProfileData, buildArtistProfileData, canAnnouncementMessage, canCopyMessage,
        canReactMessage, canReplyMessage, canUnsendMessage, chatDropScopeRef, clearMessageLongPress,
        closeImageModal, closeMessageMenu, closeProfileModal, closeReactionDetail, displayMessages,
        groupedSidePanelArtists, handleAvatarClick, handleComposerSent, handleCopyMessage,
        handleJumpToReplyMessage, handleMentionClick, handleMessageContextMenu,
        handleMessageMenuReactionSelect, handleMessageTouchStart, handleOpenInlinePartialCopy,
        handleSelectAnnouncementMessage, handleSelectReplyMessage, handleToggleAnnouncementMessage,
        handleUnsendMessage, hasMoreMessages, imageItems, imgModalIndex, imgModalOpen, inlineCopyMessageId,
        isAnnouncementListOpen, isAnnouncedMessage, isDragActive, isMemberModalOpen,
        isReactionDetailOpen, isReactionPending, isSidePanelOpen, latestAnnouncement,
        mentionCandidates, messageMenu, messageMenuRef, messagesContainerRef, members, noteCount,
        onMobileBack, openAdminProfile, openArtistProfile, openImageModal, openReactionDetail,
        overlayRect, participantCount, profileTarget, reactionDetailGroups, reactionDetailLoading,
        readOnly, refreshNoteCount, renderPartialCopyContent, replyDraft, setIsAnnouncementListOpen,
        setIsMemberModalOpen, setIsSidePanelOpen, setReplyDraft, setShowNotes,
        showDelayedInitialLoading, showInlineLoadingSpinner, showMobileBackButton, showNextImage,
        showNotes, showPrevImage, shouldShowEmptyMessages, thread, threadId, threadSummary,
        toggleReaction, unpinAnnouncement, unsendingMessageId,
      }}
    />
  );

};

export default ArtistGroupChatPane;
